import { useState } from "react";
import { authAxios as ax } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const langBadgeColors = {
  en: "bg-[#002FA7]/10 text-[#002FA7]",
  es: "bg-[#0A0A0A]/10 text-[#0A0A0A]",
  fr: "bg-[#00C853]/10 text-[#00C853]",
  ht: "bg-[#FFB800]/10 text-[#FFB800]",
};

export default function ScriptsTab({ t, scripts, reload }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [newScript, setNewScript] = useState({
    title: "", project_id: "general", content_en: "", content_es: "",
    content_fr: "", content_ht: "", category: "general",
  });
  const [editScript, setEditScript] = useState(null);

  const createScript = async () => {
    if (!newScript.title.trim()) return toast.error("Title required");
    try {
      await ax.post(`${API}/admin/scripts`, newScript);
      toast.success("Script created");
      setNewScript({ title: "", project_id: "general", content_en: "", content_es: "", content_fr: "", content_ht: "", category: "general" });
      setCreateOpen(false);
      reload();
    } catch { toast.error("Failed to create script"); }
  };

  const saveEdit = async () => {
    try {
      const payload = {
        title: editScript.title,
        category: editScript.category,
        content: editScript.content,
      };
      await ax.put(`${API}/admin/scripts/${editScript.script_id}`, payload);
      toast.success("Script updated");
      setEditScript(null);
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteScript = async (s) => {
    if (!window.confirm(`Delete script "${s.title}"?`)) return;
    try {
      await ax.delete(`${API}/admin/scripts/${s.script_id}`);
      toast.success("Script deleted");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="scripts-tab">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold">{t("admin.script_manager")}</h3>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white h-9 text-xs font-bold tracking-[0.1em] uppercase" data-testid="create-script-btn">
              <Plus size={14} className="mr-1" /> {t("admin.create_script")}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl">
            <DialogHeader><DialogTitle>{t("admin.create_script")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input className="rounded-none" placeholder="Script Title" value={newScript.title} onChange={(e) => setNewScript({ ...newScript, title: e.target.value })} data-testid="script-title-input" />
              <Input className="rounded-none" placeholder="Category" value={newScript.category} onChange={(e) => setNewScript({ ...newScript, category: e.target.value })} data-testid="script-category-input" />
              {["en", "es", "fr", "ht"].map((lang) => (
                <div key={lang}>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">{lang.toUpperCase()}</label>
                  <Textarea className="rounded-none h-16" placeholder={`Content in ${lang.toUpperCase()}`} value={newScript[`content_${lang}`]} onChange={(e) => setNewScript({ ...newScript, [`content_${lang}`]: e.target.value })} data-testid={`script-content-${lang}`} />
                </div>
              ))}
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={createScript} data-testid="script-submit-btn">{t("common.create")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4">
        {scripts.map((s) => (
          <div key={s.script_id} className="border border-[#E5E7EB] bg-white p-5" data-testid={`script-${s.script_id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <h4 className="font-semibold text-[#0A0A0A]">{s.title}</h4>
                <Badge variant="outline" className="rounded-none text-[10px] font-bold mt-1">{s.category}</Badge>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="rounded-none h-7" onClick={() => setEditScript({ ...s, content: { ...s.content } })} data-testid={`edit-script-${s.script_id}`}>
                  <Pencil size={12} />
                </Button>
                <Button size="sm" variant="outline" className="rounded-none h-7 text-[#FF2A2A] hover:bg-[#FF2A2A]/10" onClick={() => deleteScript(s)} data-testid={`delete-script-${s.script_id}`}>
                  <Trash2 size={12} />
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(s.content || {}).map(([lang, text]) => (
                <div key={lang} className="bg-[#F8F9FA] p-3 border border-[#E5E7EB]">
                  <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase mb-1 ${langBadgeColors[lang]}`}>{lang}</span>
                  <p className="text-xs text-[#4B5563] line-clamp-2">{text || "—"}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
        {scripts.length === 0 && <p className="text-sm text-[#9CA3AF] text-center py-6">{t("common.no_data")}</p>}
      </div>

      <Dialog open={!!editScript} onOpenChange={(o) => !o && setEditScript(null)}>
        <DialogContent className="rounded-none max-w-2xl" data-testid="edit-script-dialog">
          <DialogHeader><DialogTitle>Edit Script</DialogTitle></DialogHeader>
          {editScript && (
            <div className="space-y-3">
              <Input className="rounded-none" placeholder="Title" value={editScript.title} onChange={(e) => setEditScript({ ...editScript, title: e.target.value })} />
              <Input className="rounded-none" placeholder="Category" value={editScript.category || ""} onChange={(e) => setEditScript({ ...editScript, category: e.target.value })} />
              {["en", "es", "fr", "ht"].map((lang) => (
                <div key={lang}>
                  <label className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#9CA3AF] mb-1 block">{lang.toUpperCase()}</label>
                  <Textarea className="rounded-none h-16" value={editScript.content?.[lang] || ""} onChange={(e) => setEditScript({ ...editScript, content: { ...editScript.content, [lang]: e.target.value } })} data-testid={`edit-script-${lang}`} />
                </div>
              ))}
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#00227A] text-white w-full h-9 text-xs font-bold tracking-[0.1em] uppercase" onClick={saveEdit} data-testid="edit-script-save">Save</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
